import React, { useCallback, useState } from 'react'
import { Modal, message, Alert, Button, Form, Input, Switch, InputNumber } from 'antd'
import { AuthChecker, CommonSelector } from '@components'
import { pick, debounce } from 'lodash'
import {
  CallRobotSourceEnum,
} from '@types'
import CosUpload from '../COSUpload'
import { publicTag, t3cAgentList } from '../CommonSelector/configs'
import { createRobotCallRoll } from '@services/robot'
import PhoneNumberSelector from './PhoneNumberSelector'

interface T3CCallRobotProps {
  filterCondition?: any
  loading?: any
  selectedLeadList?: any
  filteredLeadList?: any
  sourceType?: number
  selectedCids?: string[]
  filterType?: number
  reload?: Function
}

const InputNumberWrapper: React.FC<{ placeholder: string; value?: number; onChange?: (value: number | null) => void }> = (props) => {
  const { placeholder, value, onChange } = props
  return (
    <div>
      <InputNumber value={value} onChange={onChange} /> {placeholder}
    </div>
  )
}

export const T3CCallRobot: React.FC<React.PropsWithChildren<T3CCallRobotProps>> = (props) => {
  const {
    filterCondition,
    loading,
    selectedLeadList,
    reload,
    filteredLeadList,
    sourceType,
    selectedCids,
    filterType,
  } = props

  const [form] = Form.useForm()
  const [showRobotOutBoundForm, setShowRobotOutBoundForm] = useState<boolean>(false)
  const [submitLoading, setSubmitLoading] = useState<boolean>(false)

  const handleCreateRobotCallRoll = debounce(async (values) => {
    const param = {
      provider: 't3c',
      rollTitle: values.rollTitle,
      agentId: values.agentId,
      excludeNamedCustomer: values.excludeNamedCustomer,
      rollDescription: values.rollDescription,
      ...(values.callNumbers && values.callNumbers.length > 0 ? { callNumbers: values.callNumbers } : {}),
      filterType,
      filterCondition: sourceType === CallRobotSourceEnum.CALL_RECORD ? values?.uploadFileKey?.[0] : filterCondition,
      filterRestriction: pick(values, ['callOutDay', 'answeredDay', 'publicTags']),
      sourceType,
    }

    setSubmitLoading(true)
    const [, err] = await createRobotCallRoll(param)
    setSubmitLoading(false)

    if (err) {
      message.info(`机器人名单创建失败${err.message}`)
      return
    }
    message.info('提交成功，稍后请留意名单状态')
    reload && reload()
    setShowRobotOutBoundForm(false)
    form.resetFields()
  }, 500)

  const onClickSubmit = useCallback(() => {
    form.validateFields()
      .then((values) => {
        handleCreateRobotCallRoll(values)
      })
      .catch((errorInfo) => {
        console.log('表单验证失败:', errorInfo)
      })
  }, [form, handleCreateRobotCallRoll])

  const handleCancel = () => {
    setShowRobotOutBoundForm(false)
    form.resetFields()
  }

  let tipsDom = null

  if (sourceType === CallRobotSourceEnum.LEAD_LIST || sourceType === CallRobotSourceEnum.LEAD_LIST_SENT) {
    tipsDom = selectedLeadList?.length > 0
      ? (
        <Alert
          message={
            <>当前勾选
              <span style={{ color: 'red' }}>{selectedLeadList?.length}</span>
              条线索,去重后按<span style={{ color: 'red' }}>{filteredLeadList?.length}</span>个客户提交外呼名单
            </>
          }
          type="info" style={{ margin: '10px' }}
        />
      )
      : (<Alert message="当前按筛选条件的全量客户提交外呼名单,请留意客户数量" type="info" style={{ margin: '10px' }} />)
  } else if (sourceType === CallRobotSourceEnum.CUSTOMER_LIST) {
    tipsDom = selectedCids?.length > 0
      ? (<Alert message={<>当前按勾选的<span style={{ color: 'red' }}>{selectedCids?.length}</span>个客户提交外呼名单</>} type="info" style={{ margin: '10px' }} />)
      : (<Alert message="当前按筛选条件的全量客户提交外呼名单,请留意客户数量" type="info" style={{ margin: '10px' }} />)
  } else if (sourceType === CallRobotSourceEnum.CALL_RECORD) {
    tipsDom = (
      <Alert
        message={
          <>注意:1、请勿修改模板表头 <br /> <div style={{ marginLeft: 42 }}>2、excel表格不超过2万个客户</div></>
        } type="info" style={{ margin: '10px' }}
      />
    )
  }

  return (
    <>
      <AuthChecker points="robotOutBound">
        <Button className="ml10" onClick={() => setShowRobotOutBoundForm(true)}>
          AI智能外呼
        </Button>
      </AuthChecker>
      <Modal
        open={showRobotOutBoundForm}
        width={800}
        title="AI智能外呼"
        onCancel={handleCancel}
        onOk={onClickSubmit}
        okButtonProps={{ loading: submitLoading || loading }}
        destroyOnClose
      >
        {tipsDom}
        <Form
          form={form}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 19 }}
          initialValues={{ excludeNamedCustomer: true }}
          preserve={false}
        >
          <Form.Item
            name="rollTitle"
            label="名单标题"
            rules={[{ required: true, message: '请输入名单标题' }]}
          >
            <Input maxLength={20} placeholder="请输入,最多20字" />
          </Form.Item>

          <Form.Item
            name="agentId"
            label="关联智能体"
            rules={[{ required: true, message: '请选择关联智能体' }]}
          >
            <CommonSelector {...t3cAgentList} placeholder="请选择" />
          </Form.Item>

          <Form.Item
            name="callNumbers"
            label="主叫号码"
            rules={[{ required: true, message: '请选择主叫号码' }]}
          >
            <PhoneNumberSelector placeholder="请选择主叫号码" />
          </Form.Item>

          <Form.Item
            name="excludeNamedCustomer"
            label="过滤有效跟进人"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="callOutDay"
            label="过滤已外呼"
          >
            <InputNumberWrapper placeholder="天内有机器人外呼则过滤不进入外呼名单" />
          </Form.Item>

          <Form.Item
            name="answeredDay"
            label="过滤已接通"
          >
            <InputNumberWrapper placeholder="天内有机器人外呼且接通则过滤不进入外呼名单" />
          </Form.Item>

          <Form.Item
            name="publicTags"
            label="过滤公共标签"
          >
            <CommonSelector {...publicTag} mode="multiple" />
          </Form.Item>

          <Form.Item
            name="rollDescription"
            label="名单描述"
          >
            <Input.TextArea rows={3} maxLength={50} placeholder="请输入,最多50字" />
          </Form.Item>

          {sourceType === CallRobotSourceEnum.CALL_RECORD && (
            <Form.Item
              name="uploadFileKey"
              label="批量上传"
              rules={[{ required: true, message: '请上传文件' }]}
            >
              <CosUpload multiple={false} maxCount={1} isShowUploadTemplate size={10} accept=".xlsx,.xls" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  )
}
